import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BoardWorkspace from "@/components/BoardWorkspace";
import ProjectImportDialog, { importError } from "@/components/ProjectImportDialog";
import { ProjectTransferPanel } from "@/components/ProjectTransferPanel";
import CompanyExportPanel from "@/components/CompanyExportPanel";
import { saveBlob } from "@/native/files";
import { useAuth } from "@/auth/AuthContext";
import { getProjectModuleAvailability } from "@/utils/projectFeatures";
import "@/styles/data-transfer.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/data-transfer`;
export default function ImportExportPage() {
  const { user } = useAuth();
  const features = user?.features || {};
  const canArchive = user?.role === "owner_admin";
  const modules = getProjectModuleAvailability(features);
  const [tab, setTab] = useState("import");
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [projectId, setProjectId] = useState("");
  const [scope, setScope] = useState("all");
  const [selected, setSelected] = useState([]);
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState(["projects"]);
  const availableCategories = categories.filter((key) => key === 'projects' || modules[key]);
  const [format, setFormat] = useState("xlsx");
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const load = useCallback(async () => {
    setLoadError("");
    try { setProjects((await axios.get(`${API}/options`)).data.projects); setLoaded(true); }
    catch (error) { setLoadError(importError(error)); setLoaded(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const visible = projects.filter((p) => (!city || p.city === city) && (!status || p.lifecycle_state === status));
  const exportIds = selected.filter((id) => visible.some((p) => p.id === id));
  const searched = visible.filter((p) => `${p.project_code} ${p.name}`.toLowerCase().includes(search.toLowerCase()));
  const exportFile = async () => {
    if (busy) return;
    setBusy(true); setMessage("");
    try {
      const response = await axios.post(`${API}/export`, { scope, project_ids: scope === "selected" ? exportIds : [], city, status, categories: availableCategories, format }, { responseType: "blob" });
      const extension = format === "xlsx" ? "xlsx" : availableCategories.length === 1 ? "csv" : "zip";
      const result = await saveBlob(response.data, `jfm-export.${extension}`);
      if (result.status === "cancelled") return;
      setMessage("Your export is ready.");
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        try { error.response.data = JSON.parse(await error.response.data.text()); } catch { /* Fall back to readable request error. */ }
      }
      setMessage(importError(error));
    } finally { setBusy(false); }
  };
  return <BoardWorkspace eyebrow="Settings" title="Import & Export" subtitle="One place to bring information in and take it out.">

    <div className="data-settings">
      <Link to="/settings">Back to Settings</Link>
      <nav className="data-tabs" aria-label="Import and export actions">
        {[["import", "Import new information"], ["export", "Simple export"], ["update", "Update existing records"], ...(canArchive ? [["archive", "Full company export"]] : [])].map(([key, label]) => <button key={key} type="button" aria-pressed={tab === key} disabled={busy || workflowBusy} onClick={() => setTab(key)}>{label}</button>)}
      </nav>
      {loadError && <p role="alert">{loadError} <button type="button" onClick={load}>Try loading projects again</button></p>}
      {tab === "archive" && canArchive && <CompanyExportPanel />}
      {tab === "import" && <ProjectImportDialog projects={projects} onImported={load} onBusyChange={setWorkflowBusy} features={features} />}
      {tab === "export" && <section className="data-card" aria-label="Simple export">
        <h2>Simple export</h2><p>Download readable project, locate and permit information. Choose Excel or CSV. Multiple CSV tables download together in a ZIP file.</p>
        <p>This is a report for sharing. To change existing records, use “Update existing records.” Project details include shared General Project Notes and their follow-ups. Photos and attached files are not included.</p>
        <fieldset disabled={busy || !loaded}>
          <div className="data-form-grid">
            <label>Projects to export<select value={scope} onChange={(e) => setScope(e.target.value)}><option value="all">All matching projects</option><option value="selected">Choose projects</option></select></label>
            <label>City filter<select value={city} onChange={(e) => setCity(e.target.value)}><option value="">All cities</option>{[...new Set(projects.map((p) => p.city).filter(Boolean))].sort().map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Status filter<select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{[...new Set(projects.map((p) => p.lifecycle_state).filter(Boolean))].sort().map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}</select></label>
            <label>File format<select value={format} onChange={(e) => setFormat(e.target.value)}><option value="xlsx">Excel (.xlsx)</option><option value="csv">CSV</option></select></label>
          </div>
          {scope === "selected" && <><label>Find a project<input value={search} onChange={(e) => setSearch(e.target.value)} /></label>
            <div className="project-choices">{searched.map((p) => <label className="check-label" key={p.id}><input type="checkbox" checked={selected.includes(p.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, p.id] : selected.filter((id) => id !== p.id))} /> {p.project_code} · {p.name}</label>)}</div>
          </>}
          <p>{scope === "all" ? visible.length : exportIds.length} projects selected by these filters</p>
          <h3>Include</h3>{[["projects", "Project details"], ["locates", "Locates / 811 tickets"], ["permits", "Permits"]].filter(([key]) => key === 'projects' || modules[key]).map(([key, label]) => <label className="check-label" key={key}><input type="checkbox" checked={categories.includes(key)} onChange={(e) => setCategories(e.target.checked ? [...categories, key] : categories.filter((v) => v !== key))} /> {label}</label>)}
          <button type="button" onClick={exportFile} disabled={!availableCategories.length || !(scope === "all" ? visible.length : exportIds.length) || busy}>{busy ? "Preparing…" : "Download export"}</button>
        </fieldset>
        {message && <p role="status">{message}</p>}
      </section>}
      {tab === "update" && <section className="data-card" aria-label="Update existing records">
        <h2>Update existing records</h2><p>Choose one project, export its editing template, then upload your changes for review. Keep the matching and version columns intact. Simple exports cannot be used to update records.</p>
        <label>Project to update<select value={projectId} disabled={!loaded || workflowBusy} onChange={(e) => setProjectId(e.target.value)}><option value="">Choose a project</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.project_code} · {p.name}</option>)}</select></label>
        {projectId && <ProjectTransferPanel key={projectId} canTransfer projectId={projectId} embedded onBusyChange={setWorkflowBusy} />}
      </section>}
    </div>
  </BoardWorkspace>;
}

